import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { OrgUnitKind, ScopeKind, isAssigned } from '@/enums/group'
import type { IGroupProperty, IGroupTree, IOrgUnit } from '@/types/Group'

/**
 * WHAT THE CONSOLE IS LOOKING AT — the one thing that replaces the property switcher.
 *
 * # Why it is not a property switcher
 *
 * The reception portal works in one house all day and reloads the page when it changes
 * (root CLAUDE.md). The administration never works in one house: the whole reason this
 * console exists is the consolidated view, so the question is not "which hotel" but
 * "how much of the group" — everything, one region or brand, one company, or a handful
 * of houses somebody picked.
 *
 * # Nothing reloads
 *
 * The selection is reactive state and no screen is remounted for it: a view reads it
 * and redraws, which is what makes comparing a region against the group a matter of two
 * clicks rather than two page loads.
 *
 * # It is in the ADDRESS, and remembered
 *
 * `?scope=unit:<id>` is the truth of the page (`libs/scopeQuery.ts` keeps the two in
 * step), so a link pasted into a message opens on the houses the sender was looking at.
 * localStorage is the fallback for an address that carries none — somebody who runs a
 * region comes back to their region tomorrow morning. A selection whose unit, company
 * or house has since gone, or has left this account's scope, is dropped on the next
 * read of the tree rather than sent to a server that would refuse it (`reconcile`).
 */

const STORAGE_KEY = 'group.scope'

interface Selection {
    kind:        ScopeKind
    unitId:      string
    entityId:    string
    propertyIds: string[]
}

const emptySelection = (): Selection => ({
    kind:        ScopeKind.Group,
    unitId:      '',
    entityId:    '',
    propertyIds: [],
})

/** One company, as the scope selector needs it: a name and the id behind it. */
export interface IScopeEntity {
    id:   string
    name: string
}

/**
 * The selection a `?scope=` value means, or the whole group for anything unreadable.
 *
 * Unreadable includes a truncated id and a kind nobody has ever written, because the
 * value arrives from an address bar and a pasted link is edited by hand more often than
 * anybody admits.
 */
export const parseScopeQuery = (raw?: string | null): Selection => {
    const value = (raw ?? '').trim()

    if (!value) return emptySelection()

    const separator = value.indexOf(':')

    if (separator < 1) return emptySelection()

    const kind = value.slice(0, separator)
    const rest = value.slice(separator + 1)

    if (!rest) return emptySelection()

    switch (kind) {
        case ScopeKind.Unit:   return { ...emptySelection(), kind: ScopeKind.Unit,   unitId: rest }
        case ScopeKind.Entity: return { ...emptySelection(), kind: ScopeKind.Entity, entityId: rest }

        case ScopeKind.Properties: {
            const ids = rest.split(',').map(id => id.trim()).filter(Boolean)

            return ids.length
                ? { ...emptySelection(), kind: ScopeKind.Properties, propertyIds: ids }
                : emptySelection()
        }

        default: return emptySelection()
    }
}

/** The `?scope=` value a selection is. Empty for the whole group, which needs no word. */
const toScopeQuery = (selection: Selection): string => {
    switch (selection.kind) {
        case ScopeKind.Unit:       return `${ScopeKind.Unit}:${selection.unitId}`
        case ScopeKind.Entity:     return `${ScopeKind.Entity}:${selection.entityId}`
        case ScopeKind.Properties: return `${ScopeKind.Properties}:${selection.propertyIds.join(',')}`
        default:                   return ''
    }
}

/** What was remembered, or the whole group. A malformed key is not worth a crash. */
const readStored = (): Selection => {
    try {
        return parseScopeQuery(localStorage.getItem(STORAGE_KEY))
    } catch {
        return emptySelection()
    }
}

export const useScopeStore = defineStore('scope', () => {
    const selection = ref<Selection>(readStored())

    /** The group as `GET /group/tree` answers it. Null until the shell has read it. */
    const tree = ref<IGroupTree | null>(null)

    const units      = computed((): IOrgUnit[]       => tree.value?.units ?? [])
    const properties = computed((): IGroupProperty[] => tree.value?.properties ?? [])

    /** The name of the group itself, for the button that says "everything". */
    const rootName = computed((): string => tree.value?.root.name ?? '')

    /** Everything below the root — a region or a brand, never the root itself. */
    const branches = computed((): IOrgUnit[] =>
        units.value.filter(unit => unit.kind !== OrgUnitKind.Group))

    /**
     * The companies, as the houses name them.
     *
     * Read out of the tree rather than from `GET /group/legal-entities`: the selector
     * exists to narrow a set of HOUSES, and a company that owns none narrows it to
     * nothing. „Družbe“ reads the register itself, because a company entered this
     * morning has to appear there before it owns anything.
     */
    const entities = computed((): IScopeEntity[] => {
        const byId = new Map<string, string>()

        for (const house of properties.value) {
            if (isAssigned(house.legal_entity_id) && !byId.has(house.legal_entity_id)) {
                byId.set(house.legal_entity_id, house.legal_entity_name)
            }
        }

        return [...byId].map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name))
    })

    const kind     = computed((): ScopeKind => selection.value.kind)
    const unitId   = computed((): string => selection.value.unitId)
    const entityId = computed((): string => selection.value.entityId)

    /** What the address bar carries. Empty for the whole group. */
    const queryValue = computed((): string => toScopeQuery(selection.value))

    /**
     * The houses this selection covers.
     *
     * A unit covers its own houses and those of its children, which is the whole depth
     * the tree has. The set is the console's own resolution and is right for as long as
     * the tree it was read from is: every group READ this console makes is of the whole
     * tenant, and the narrowing happens here. The packages that read figures per house
     * send the unit or the company as itself, so that a hotel opened in a region
     * tomorrow is inside the answer tomorrow.
     */
    const propertyIds = computed((): string[] => selectedProperties.value.map(house => house.id))

    /** The houses of the selection, in the order the tree answered them. */
    const selectedProperties = computed((): IGroupProperty[] => {
        switch (selection.value.kind) {
            case ScopeKind.Unit: {
                const covered = new Set([
                    selection.value.unitId,
                    ...units.value
                        .filter(unit => unit.parent_id === selection.value.unitId)
                        .map(unit => unit.id),
                ])

                return properties.value.filter(house => covered.has(house.org_unit_id))
            }

            case ScopeKind.Entity:
                return properties.value.filter(house => house.legal_entity_id === selection.value.entityId)

            case ScopeKind.Properties: {
                const wanted = new Set(selection.value.propertyIds)

                return properties.value.filter(house => wanted.has(house.id))
            }

            default:
                return properties.value
        }
    })

    const persist = () => {
        try {
            localStorage.setItem(STORAGE_KEY, queryValue.value)
        } catch {
            // A browser with storage switched off still has to work; what it loses is
            // that tomorrow morning opens on the group rather than on the region.
        }
    }

    const apply = (value: Selection) => {
        selection.value = value
        persist()
    }

    const selectGroup      = () => apply(emptySelection())
    const selectUnit       = (id: string) => apply({ ...emptySelection(), kind: ScopeKind.Unit, unitId: id })
    const selectEntity     = (id: string) => apply({ ...emptySelection(), kind: ScopeKind.Entity, entityId: id })

    const selectProperties = (ids: string[]) => {
        // An empty pick is the whole group and not an empty group: a filter that
        // selects nothing would leave every screen honestly blank, which reads as a
        // service that answered nothing.
        if (!ids.length) return selectGroup()

        apply({ ...emptySelection(), kind: ScopeKind.Properties, propertyIds: [...ids] })
    }

    /** What an address bar said. The same reconciliation as a stored selection gets. */
    const selectFromQuery = (raw?: string | null) => {
        apply(parseScopeQuery(raw))
        reconcile()
    }

    /**
     * The group, and what the remembered selection still means in it.
     *
     * Called after every read of `GET /group/tree`. A unit, a company or a house that
     * has gone — or that this account may no longer see — silently becomes the whole
     * scope: the alternative is a selector standing on an id the server refuses, on
     * every screen, with no way back except clearing the browser.
     */
    const setTree = (value: IGroupTree) => {
        tree.value = value
        reconcile()
    }

    const reconcile = () => {
        // Nothing to reconcile against yet. Without this, a selection read out of the
        // address bar on a cold load would be dropped for not appearing in a tree that
        // has not arrived — which is every deep link into a region.
        if (!tree.value) return

        const current = selection.value

        if (current.kind === ScopeKind.Unit && !units.value.some(unit => unit.id === current.unitId)) {
            return selectGroup()
        }

        if (current.kind === ScopeKind.Entity && !entities.value.some(entity => entity.id === current.entityId)) {
            return selectGroup()
        }

        if (current.kind === ScopeKind.Properties) {
            const known = new Set(properties.value.map(house => house.id))
            const kept  = current.propertyIds.filter(id => known.has(id))

            if (kept.length !== current.propertyIds.length) return selectProperties(kept)
        }
    }

    return {
        tree, units, branches, entities, properties, rootName,
        kind, unitId, entityId, queryValue, propertyIds, selectedProperties,
        setTree, selectGroup, selectUnit, selectEntity, selectProperties, selectFromQuery,
    }
})
